// Typisierter fetch-Wrapper. Haengt Bearer-Token automatisch an.
// Bei 401 wird Auth-Storage geleert und ein DOM-Event dispatched
// damit der AuthContext den User auf null setzt.

import { clearStoredAuth, readStoredAuth } from "../auth/storage.js";

const BASE = import.meta.env.VITE_BACKEND_URL ?? "";

/** Backend-Origin (leer = same-origin via Reverse-Proxy). Fuer URLs ausserhalb des fetch-Wrappers, z.B. <img src>. */
export const API_BASE = BASE;

export const AUTH_EXPIRED_EVENT = "ms-whiteboard:auth-expired";

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code?: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

interface ApiErrorBody {
  code?: string;
  message?: string;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const auth = readStoredAuth();
  const headers: Record<string, string> = {
    Accept: "application/json",
    // Nur JSON-Bodies bekommen application/json. FormData (Upload) NICHT — der Browser
    // setzt dort den multipart-Boundary-Header selbst.
    ...(typeof init.body === "string" ? { "Content-Type": "application/json" } : {}),
    ...(auth ? { Authorization: `Bearer ${auth.token}` } : {}),
    ...((init.headers as Record<string, string> | undefined) ?? {}),
  };

  const res = await fetch(`${BASE}${path}`, { ...init, headers });

  if (res.status === 401 && auth) {
    clearStoredAuth();
    window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT));
  }

  if (!res.ok) {
    let body: ApiErrorBody = {};
    try {
      body = (await res.json()) as ApiErrorBody;
    } catch {
      // ignore
    }
    throw new ApiError(body.message ?? res.statusText, res.status, body.code);
  }

  // 204 No Content
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "POST", body: JSON.stringify(body) }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "PATCH", body: JSON.stringify(body) }),
  del: <T>(path: string) => request<T>(path, { method: "DELETE" }),
  upload: <T>(path: string, form: FormData) => request<T>(path, { method: "POST", body: form }),
};
